import React from "react";
// ANT DESIGN COMPONENTS
import { Tag } from "antd";
// DEVICE DETECT
import { isMobile } from "react-device-detect";
// I18N TRANSLATION
import { useTranslation } from "react-i18next";
// FONT AWESOME LIBRARY AND ICONS
import {
  faBook,
  faNoteSticky,
  faPlugCircleCheck,
  faWrench,
} from "@fortawesome/free-solid-svg-icons";
// REDUX
import { useSelector } from "react-redux";
// COMPONENTS
import DrawerRowItem from "./DrawerRowItem";
import TableColumnLiftType from "../table/TableColumnLiftType";
import TableColumnAuthType from "../table/TableColumnAuthType";
import DrawerRowDivider from "./DrawerRowDivider";
// INTERFACES
import { DrawerAdapterInformationInterface } from "../../../interfaces/Global.interface";
import { RootState } from "../../../app/store";
import { AdapterType } from "../../../flatbuffers/generated/archive/adapter-type";

function DrawerAdapterInformation({ data }: DrawerAdapterInformationInterface) {
  // ************************************************ */
  // GLOBAL VARIABLES ******************************* */
  const [t] = useTranslation("global");

  // ************************************************ */
  // USE STATE VARIABLES **************************** */

  // ************************************************ */
  // REDUX SLICE VARIABLES ************************** */
  const { theme } = useSelector((state: RootState) => state.home);

  // ************************************************ */
  // SERVICES AND API CALLS ************************* */

  // ************************************************ */
  // FUNCTIONS ************************************** */

  // ************************************************* */
  // USE EFFECT ************************************** */

  // ************************************************ */
  // COMPONENT ************************************** */
  return (
    <div style={{ overflowX: "auto" }}>
      {/** ------------------------------------------------------------- */}
      {/** COMPONENT DRAWER DIVIDER */}
      <DrawerRowDivider icon={faPlugCircleCheck} title={t("adapter.adapterInformation")} />
      {/** ------------------------------------------------------------- */}
      {/** ADAPTER  */}
      <div className={`drawer__box__${theme}`}>
        {/* ID */}
        <DrawerRowItem title="ID" value={data && data.id ? String(data.id) : "--"} />
        {/* NAME */}
        <DrawerRowItem
          title={t("general.name")}
          value={data && data.name ? String(data.name) : "--"}
        />
        {/* VERSION */}
        <DrawerRowItem
          title={t("adapter.version")}
          value={data && data.version ? String(data.version) : "--"}
        />
        {/* LIFT TYPE */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              Lift {t("edgeManagement.lifts.type")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <div className="drawer__text">
              {data && data.liftType != null ? (
                <TableColumnLiftType liftType={data.liftType} />
              ) : (
                "--"
              )}
            </div>
          </div>
        </div>
        {/* PROTOCOL TYPE */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("general.protocol")}
            </p>
          </div>
          <div className="drawer__textContainer">
            <div className="drawer__text">
              <Tag color="magenta" className="drawer__typeTag">
                {data && data.protocolType != null ? String(data.protocolType) : "--"}
              </Tag>
            </div>
          </div>
        </div>
        {/* TYPE */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("adapter.type")}:
            </p>
          </div>
          <div className="drawer__textContainer">
            <div className="drawer__text">
              {data && data.type != null && (
                <>
                  {data.type === AdapterType.REST && (
                    <Tag color="blue" className="drawer__typeTag">
                      REST
                    </Tag>
                  )}
                  {data.type === AdapterType.SOAP && (
                    <Tag color="orange" className="drawer__typeTag">
                      SOAP
                    </Tag>
                  )}
                  {data.type === AdapterType.WEBSOCKET && (
                    <Tag color="green" className="drawer__typeTag">
                      WEBSOCKET
                    </Tag>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
        {/* AUTH TYPE */}
        <div className="generalStyles__flex">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("adapter.authType")}:
            </p>
          </div>
          <div className="drawer__textContainer">
            <div className="drawer__text">
              {data && data.authType != null && <TableColumnAuthType authType={data.authType} />}
            </div>
          </div>
        </div>
      </div>
      {/** ------------------------------------------------------------- */}
      {/** COMPONENT DRAWER DIVIDER */}
      <br />
      <DrawerRowDivider icon={faNoteSticky} title={t("adapter.dictionary")} />
      <div className={`drawer__box__${theme}`}>
        {/* DICTIONARY */}
        <div className="generalStyles__flex drawer__container">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("adapter.dictionary")}
            </p>
          </div>
          <div className="drawer__textContainer" />
        </div>
        <div className="generalStyles__flex drawer__container">
          <div>
            <pre className="drawer__text">
              {data && data.dictionaryJson ? String(data.dictionaryJson) : "--"}
            </pre>
          </div>
        </div>
      </div>
      {/** ------------------------------------------------------------- */}
      {/** COMPONENT DRAWER DIVIDER */}
      <br />
      <DrawerRowDivider icon={faBook} title={t("adapter.interface")} />
      <div className={`drawer__box__${theme}`}>
        {/* INTERFACE */}
        <div className="generalStyles__flex drawer__container">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("adapter.interface")}
            </p>
          </div>
          <div className="drawer__textContainer" />
        </div>
        <div className="generalStyles__flex drawer__container">
          <div>
            <pre className="drawer__text">
              {data && data.interfaceJson ? String(data.interfaceJson) : "--"}
            </pre>
          </div>
        </div>
      </div>
      {/** ------------------------------------------------------------- */}
      {/** COMPONENT DRAWER DIVIDER */}
      <br />
      <DrawerRowDivider icon={faWrench} title={t("adapter.bacnetConfig")} />
      <div className={`drawer__box__${theme}`}>
        {/* BACNET  */}
        <div className="generalStyles__flex drawer__container">
          <div>
            <p className={isMobile ? "drawer__titleMobile" : "drawer__title"}>
              {t("adapter.bacnetConfig")}
            </p>
          </div>
          <div className="drawer__textContainer" />
        </div>
        <div className="generalStyles__flex drawer__container">
          <div>
            <pre className="drawer__text">
              {data && data.bacnetConfig ? JSON.stringify(data.bacnetConfig, null, 2) : "--"}
            </pre>
          </div>
        </div>
      </div>
    </div>
  );
}

export default DrawerAdapterInformation;
